/**
 * Centralized TanStack Query key factories for core CRM entities.
 *
 * All new query/mutation hooks should import keys from here. Prevents cache
 * misses caused by key divergence (e.g. a mutation invalidating ['contacts']
 * while the query subscribes to ['contact-detail', id] — different prefixes,
 * no prefix match, no invalidation).
 *
 * Two flavors of key per entity:
 *  - `.infinite(ownerId, workspaceId)` — FULL key used by a specific useQuery.
 *    Partitions cache per owner/workspace so different users see different data.
 *  - `.infiniteAll()` — PREFIX used by mutations to invalidate every partition
 *    (ownerId/workspaceId don't matter when a contact is mutated — all lists
 *    that contain it should refresh).
 *
 * Keys are typed as `const` tuples so TypeScript can track exact shapes
 * through useQuery/useMutation generics.
 */

type ListViewParams = {
  filters: unknown;
  sortConfig: unknown;
  searchQuery: string;
  dealsView: string;
  userId?: string | null;
  ownerId?: string | null;
  workspaceId?: string | null;
};

type AssistRecordSearchType = 'contact' | 'company' | 'deal' | 'none';
type RecordHistoryEntityType = 'contact' | 'company' | 'deal';

function recordHistoryEntityRoot(entityType: RecordHistoryEntityType) {
  if (entityType === 'contact') return 'contacts';
  if (entityType === 'company') return 'companies';
  return 'deals';
}

/**
 * Important structural decision: the list-view cache for each entity lives
 * UNDER that entity's prefix (e.g. `['contacts', 'listView', ...]`).
 *
 * Why it matters: TanStack Query invalidates by prefix match. Any existing
 * mutation that calls `invalidateQueries({ queryKey: ['contacts'] })` —
 * including the ~30 peripheral hooks (enrichment, record assignment,
 * bulk scoring, CSV import, etc.) — automatically invalidates the contact
 * list view without having to add a separate `listView` invalidation call.
 * Same for companies and deals.
 *
 * The old `['listView']` top-level key forced every peripheral hook to add
 * an extra invalidation line; piggybacking on the entity prefix eliminates
 * that churn and the risk of a future hook silently skipping the list view.
 */
export const queryKeys = {
  contacts: {
    all: () => ['contacts'] as const,
    infinite: (ownerId?: string | null, workspaceId?: string | null) =>
      ['contacts', 'infinite', ownerId ?? 'all', workspaceId ?? 'all'] as const,
    infiniteAll: () => ['contacts', 'infinite'] as const,
    dropdown: (workspaceId?: string | null) =>
      ['contacts', 'dropdown', workspaceId ?? 'default'] as const,
    dropdownAll: () => ['contacts', 'dropdown'] as const,
    search: (term: string, workspaceId?: string | null) =>
      ['contacts', 'search', workspaceId ?? 'default', term] as const,
    byId: (id: string, workspaceId?: string | null) =>
      ['contacts', 'byId', workspaceId ?? 'default', id] as const,
    byIds: (ids: readonly string[], workspaceId?: string | null) =>
      ['contacts', 'byIds', workspaceId ?? 'default', ...ids] as const,
    detail: (id: string, workspaceId?: string | null) =>
      ['contact-detail', id, workspaceId ?? 'default'] as const,
    detailAll: () => ['contact-detail'] as const,
    listView: (params: ListViewParams) => ['contacts', 'listView', params] as const,
    listViewAll: () => ['contacts', 'listView'] as const,
    pendingScoring: (workspaceId?: string | null) =>
      ['contacts', 'pendingScoring', workspaceId ?? 'default'] as const,
    pendingScoringAll: () => ['contacts', 'pendingScoring'] as const,
    emailMergeContext: (contactIds: readonly string[]) =>
      ['contacts', 'emailMergeContext', ...contactIds] as const,
  },
  companies: {
    all: () => ['companies'] as const,
    infinite: (ownerId?: string | null, workspaceId?: string | null) =>
      ['companies', 'infinite', ownerId ?? 'all', workspaceId ?? 'all'] as const,
    infiniteAll: () => ['companies', 'infinite'] as const,
    dropdown: (workspaceId?: string | null) =>
      ['companies', 'dropdown', workspaceId ?? 'default'] as const,
    dropdownAll: () => ['companies', 'dropdown'] as const,
    byIds: (ids: readonly string[], workspaceId?: string | null) =>
      ['companies', 'byIds', workspaceId ?? 'default', ...ids] as const,
    search: (term: string, workspaceId?: string | null) =>
      ['companies', 'search', workspaceId ?? 'default', term] as const,
    detail: (id: string, workspaceId?: string | null) =>
      ['company-detail', id, workspaceId ?? 'default'] as const,
    detailAll: () => ['company-detail'] as const,
    enrichmentReview: (id: string, workspaceId?: string | null) =>
      ['companies', 'enrichmentReview', workspaceId ?? 'default', id] as const,
    listView: (params: ListViewParams) => ['companies', 'listView', params] as const,
    listViewAll: () => ['companies', 'listView'] as const,
  },
  focusCompanies: {
    all: () => ['focus-companies'] as const,
    dashboard: (workspaceId?: string | null) =>
      ['focus-companies', 'dashboard', workspaceId ?? 'default'] as const,
  },
  companySignals: {
    all: () => ['company-signals'] as const,
    byCompany: (companyId: string) =>
      ['company-signals', 'company', companyId] as const,
  },
  opportunitySignals: {
    all: () => ['opportunity-signals'] as const,
    byOpportunity: (opportunityId: string) =>
      ['opportunity-signals', 'opportunity', opportunityId] as const,
  },
  companyContactAssociations: {
    all: () => ['company-contact-associations'] as const,
    byCompany: (companyId?: string | null) =>
      ['company-contact-associations', 'company', companyId ?? 'none'] as const,
    byContact: (contactId?: string | null) =>
      ['company-contact-associations', 'contact', contactId ?? 'none'] as const,
  },
  calendarEvents: {
    all: () => ['calendar-events'] as const,
    leadUpcoming: (userId?: string | null) =>
      ['calendar-events', 'lead-upcoming', userId ?? 'none'] as const,
  },
  timeline: {
    leadRecent: (contactId?: string | null) =>
      ['unified-timeline', contactId ?? 'none', 'lead-recent'] as const,
  },
  buckets: {
    all: () => ['buckets'] as const,
    contactAssignments: (workspaceId?: string | null, contactId?: string | null) =>
      ['contact-bucket-assignments', workspaceId ?? 'no-workspace', contactId ?? 'none'] as const,
    contactAssignmentsAll: () => ['contact-bucket-assignments'] as const,
  },
  deals: {
    all: () => ['deals'] as const,
    infinite: (
      scope: 'all' | 'owner',
      pipelineId?: string | null,
      ownerId?: string | null,
      workspaceId?: string | null,
    ) =>
      ['deals', 'infinite', scope, pipelineId ?? null, ownerId ?? 'all', workspaceId ?? null] as const,
    infiniteAll: () => ['deals', 'infinite'] as const,
    dropdown: (workspaceId?: string | null) =>
      ['deals', 'dropdown', workspaceId ?? 'default'] as const,
    dropdownAll: () => ['deals', 'dropdown'] as const,
    landedSeeds: (userId?: string | null) =>
      ['deals', 'landedSeeds', userId ?? 'anon'] as const,
    landedLookalikes: (dealId?: string | null, userId?: string | null) =>
      ['deals', 'landedLookalikes', dealId ?? 'none', userId ?? 'anon'] as const,
    canvas: (id: string) => ['deal-canvas', id] as const,
    canvasAll: () => ['deal-canvas'] as const,
    contactDeals: (contactId?: string) =>
      (contactId ? ['contact-deals', contactId] : ['contact-deals']) as readonly unknown[],
    listView: (params: ListViewParams) => ['deals', 'listView', params] as const,
    listViewAll: () => ['deals', 'listView'] as const,
    // Deals won from a sales pipeline that now live in the Clients pipeline.
    // Kept under the ['deals'] prefix so every deal write refreshes it.
    wonHandoffs: (workspaceId?: string | null, pipelineId?: string | null) =>
      ['deals', 'wonHandoffs', workspaceId ?? null, pipelineId ?? null] as const,
    wonHandoffsAll: () => ['deals', 'wonHandoffs'] as const,
  },
  recordHistory: {
    byEntity: (entityType: RecordHistoryEntityType) =>
      [recordHistoryEntityRoot(entityType), 'record-history'] as const,
    detail: (
      entityType: RecordHistoryEntityType,
      entityId: string,
      limit: number,
    ) => [
      recordHistoryEntityRoot(entityType),
      'record-history',
      entityId,
      limit,
    ] as const,
  },
  pipelines: {
    all: (userId?: string | null) =>
      (userId ? ['pipelines', userId] : ['pipelines']) as readonly unknown[],
    allPrefix: () => ['pipelines'] as const,
    stages: (pipelineId: string) => ['pipeline-stages', pipelineId] as const,
    stagesPrefix: () => ['pipeline-stages'] as const,
    compare: (userId?: string | null) =>
      ['pipeline-compare', userId ?? 'anon'] as const,
  },
  subscription: {
    tier: (userId?: string | null) =>
      ['user-tier', userId ?? 'anon'] as const,
    tierAll: () => ['user-tier'] as const,
    sync: (userId: string) =>
      ['subscription-sync', userId] as const,
  },
  clientEngagements: {
    importantConversations: (workspaceId: string | null) => ['important-conversations', workspaceId] as const,
    cohort: (workspaceId: string) => ['client-engagements', workspaceId, 'cohort'] as const,
    account: (workspaceId: string, companyId: string | null) => ['client-engagements', workspaceId, 'account', companyId] as const,
    history: (workspaceId: string, dealId: string) => ['client-engagements', workspaceId, 'deal', dealId, 'history'] as const,
  },
  clientCanvas: {
    deal: (dealId: string | null, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-canvas-deal', workspaceId, dealId]
        : ['client-canvas-deal']) as readonly unknown[],
    dealPrefix: () => ['client-canvas-deal'] as const,
    contact: (contactId: string | null, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-canvas-contact', workspaceId, contactId]
        : ['client-canvas-contact']) as readonly unknown[],
    company: (companyId: string | null, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-canvas-company', workspaceId, companyId]
        : ['client-canvas-company']) as readonly unknown[],
    stages: (pipelineId: string | null, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-pipeline-stages', workspaceId, pipelineId]
        : ['client-pipeline-stages']) as readonly unknown[],
    stakeholders: (dealId: string | null, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-canvas-stakeholders', workspaceId, dealId]
        : ['client-canvas-stakeholders']) as readonly unknown[],
    services: (dealId: string | null, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-deal-services', workspaceId, dealId]
        : ['client-deal-services']) as readonly unknown[],
    lines: (dealId: string | null, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-deal-lines', workspaceId, dealId]
        : ['client-deal-lines']) as readonly unknown[],
    linesPrefix: () => ['client-deal-lines'] as const,
    // Keyed by Account and currency because those are the two things
    // `app_validate_deal_commercial_context` matches a baseline on.
    agreementBaselines: (
      companyId: string | null | undefined,
      currency: string | null | undefined,
      workspaceId?: string | null,
    ) =>
      (workspaceId
        ? ['commercial-agreement-baselines', workspaceId, companyId ?? null, currency ?? null]
        : ['commercial-agreement-baselines']) as readonly unknown[],
    agreementBaselinesPrefix: () => ['commercial-agreement-baselines'] as const,
    // Both reads answer for the whole workspace and are filtered to one Account
    // in the hook, so the Account is part of the key rather than the query.
    projects: (
      accountCompanyId: string | null | undefined,
      workspaceId?: string | null,
    ) =>
      (workspaceId
        ? ['client-projects', workspaceId, accountCompanyId ?? null]
        : ['client-projects']) as readonly unknown[],
    projectsPrefix: () => ['client-projects'] as const,
    allocations: (
      accountCompanyId: string | null | undefined,
      workspaceId?: string | null,
    ) =>
      (workspaceId
        ? ['commercial-allocations', workspaceId, accountCompanyId ?? null]
        : ['commercial-allocations']) as readonly unknown[],
    allocationsPrefix: () => ['commercial-allocations'] as const,
    diagnostics: (
      accountCompanyId: string | null | undefined,
      workspaceId?: string | null,
    ) =>
      (workspaceId
        ? ['client-diagnostics', workspaceId, accountCompanyId ?? null]
        : ['client-diagnostics']) as readonly unknown[],
    diagnosticsPrefix: () => ['client-diagnostics'] as const,
    diagnosticSources: (diagnosticId: string | null | undefined, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-diagnostic-sources', workspaceId, diagnosticId ?? null]
        : ['client-diagnostic-sources']) as readonly unknown[],
    diagnosticSourcesPrefix: () => ['client-diagnostic-sources'] as const,
    diagnosticEvidence: (diagnosticId: string | null | undefined, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-diagnostic-evidence', workspaceId, diagnosticId ?? null]
        : ['client-diagnostic-evidence']) as readonly unknown[],
    diagnosticEvidencePrefix: () => ['client-diagnostic-evidence'] as const,
    diagnosticFindings: (diagnosticId: string | null | undefined, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-diagnostic-findings', workspaceId, diagnosticId ?? null]
        : ['client-diagnostic-findings']) as readonly unknown[],
    diagnosticFindingsPrefix: () => ['client-diagnostic-findings'] as const,
    diagnosticJobs: (diagnosticId: string | null | undefined, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-diagnostic-jobs', workspaceId, diagnosticId ?? null]
        : ['client-diagnostic-jobs']) as readonly unknown[],
    diagnosticJobsPrefix: () => ['client-diagnostic-jobs'] as const,
    diagnosticVersions: (diagnosticId: string | null | undefined, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-diagnostic-versions', workspaceId, diagnosticId ?? null]
        : ['client-diagnostic-versions']) as readonly unknown[],
    diagnosticVersionsPrefix: () => ['client-diagnostic-versions'] as const,
    diagnosticVersionDocument: (versionId: string | null | undefined, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-diagnostic-version-document', workspaceId, versionId ?? null]
        : ['client-diagnostic-version-document']) as readonly unknown[],
    diagnosticVersionDocumentPrefix: () => ['client-diagnostic-version-document'] as const,
    diagnosticOutcomes: (diagnosticId: string | null | undefined, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-diagnostic-outcomes', workspaceId, diagnosticId ?? null]
        : ['client-diagnostic-outcomes']) as readonly unknown[],
    diagnosticOutcomesPrefix: () => ['client-diagnostic-outcomes'] as const,
    playbookLearning: (playbookId: string | null | undefined, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-diagnostic-playbook-learning', workspaceId, playbookId ?? null]
        : ['client-diagnostic-playbook-learning']) as readonly unknown[],
    playbookLearningPrefix: () => ['client-diagnostic-playbook-learning'] as const,
    // The ladder is evaluated at an effective date, so two dates are two
    // different answers about the same Deal.
    activationReadiness: (
      dealId: string | null,
      effectiveFrom: string,
      workspaceId?: string | null,
    ) =>
      (workspaceId
        ? ['deal-activation-readiness', workspaceId, dealId, effectiveFrom]
        : ['deal-activation-readiness']) as readonly unknown[],
    activationReadinessPrefix: () => ['deal-activation-readiness'] as const,
    // The explicit-rebase decision exists only at the exact date the booked
    // version starts, which is also the only date the executor will accept the
    // answer at, so the date belongs in this key exactly as it does above.
    pendingRebase: (
      dealId: string | null,
      effectiveFrom: string,
      workspaceId?: string | null,
    ) =>
      (workspaceId
        ? ['agreement-pending-rebase', workspaceId, dealId, effectiveFrom]
        : ['agreement-pending-rebase']) as readonly unknown[],
    pendingRebasePrefix: () => ['agreement-pending-rebase'] as const,
    // The effective-state read answers for the whole workspace on one date, so
    // the Account and the date are both part of the key rather than the query.
    agreementTerm: (
      accountCompanyId: string | null | undefined,
      asOf: string,
      workspaceId?: string | null,
    ) =>
      (workspaceId
        ? ['account-agreement-term', workspaceId, accountCompanyId ?? null, asOf]
        : ['account-agreement-term']) as readonly unknown[],
    agreementTermPrefix: () => ['account-agreement-term'] as const,
    // The portfolio surfaces read the same contract once for every Account on
    // the screen instead of once per Account, so this is keyed by the date
    // alone. Deliberately a different key from `agreementTerm` above rather
    // than a shared one: that key carries an Account and this one does not, so
    // sharing would let whichever surface mounted first decide what the other
    // one sees — the same trap `accountBillingCoverage` below records.
    workspaceAgreementTerms: (asOf: string, workspaceId?: string | null) =>
      (workspaceId
        ? ['workspace-agreement-terms', workspaceId, asOf]
        : ['workspace-agreement-terms']) as readonly unknown[],
    workspaceAgreementTermsPrefix: () => ['workspace-agreement-terms'] as const,
    billingSchedule: (lineageId: string | null | undefined, workspaceId?: string | null) =>
      (workspaceId
        ? ['commercial-billing-schedule', workspaceId, lineageId ?? null]
        : ['commercial-billing-schedule']) as readonly unknown[],
    billingSchedulePrefix: () => ['commercial-billing-schedule'] as const,
    // Both billing reads answer for the whole workspace on one date and are
    // filtered to one Account in the hook, so the Account and the date are part
    // of the key rather than the query — the same shape `agreementTerm` uses.
    // Deliberately separate from `billingSchedule` above, which is keyed by
    // lineage and takes no as-of date: sharing one key would let whichever
    // surface mounted first decide what the other one sees.
    accountBillingCoverage: (
      accountCompanyId: string | null | undefined,
      asOf: string,
      workspaceId?: string | null,
    ) =>
      (workspaceId
        ? ['account-billing-coverage', workspaceId, accountCompanyId ?? null, asOf]
        : ['account-billing-coverage']) as readonly unknown[],
    accountBillingCoveragePrefix: () => ['account-billing-coverage'] as const,
    accountBillingSchedule: (
      accountCompanyId: string | null | undefined,
      asOf: string,
      workspaceId?: string | null,
    ) =>
      (workspaceId
        ? ['account-billing-schedule', workspaceId, accountCompanyId ?? null, asOf]
        : ['account-billing-schedule']) as readonly unknown[],
    accountBillingSchedulePrefix: () => ['account-billing-schedule'] as const,
    // The portfolio surfaces read the coverage contract once for every Account
    // on the screen instead of once per Account, so this is keyed by the date
    // alone. Deliberately a different key from `accountBillingCoverage` above,
    // for the reason recorded there: that key carries an Account and this one
    // does not, and a shared key would let whichever surface mounted first
    // decide what the other one sees.
    workspaceBillingCoverage: (asOf: string, workspaceId?: string | null) =>
      (workspaceId
        ? ['workspace-billing-coverage', workspaceId, asOf]
        : ['workspace-billing-coverage']) as readonly unknown[],
    workspaceBillingCoveragePrefix: () => ['workspace-billing-coverage'] as const,
    // A role is about one thing, so both halves of that identity are in the key.
    contextualRoles: (
      contextKind: string,
      contextId: string | null | undefined,
      workspaceId?: string | null,
    ) =>
      (workspaceId
        ? ['contact-contextual-roles', workspaceId, contextKind, contextId ?? null]
        : ['contact-contextual-roles']) as readonly unknown[],
    contextualRolesPrefix: () => ['contact-contextual-roles'] as const,
    // Coverage is evaluated at an as-of date, so two dates are two different
    // answers about the same piece of work — the same reason the activation
    // readiness key carries its effective date.
    contextualRoleCoverage: (
      contextKind: string,
      contextId: string | null | undefined,
      asOf: string,
      workspaceId?: string | null,
    ) =>
      (workspaceId
        ? ['contact-role-coverage', workspaceId, contextKind, contextId ?? null, asOf]
        : ['contact-role-coverage']) as readonly unknown[],
    contextualRoleCoveragePrefix: () => ['contact-role-coverage'] as const,
    handoverRuns: (accountCompanyId: string | null | undefined, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-handover-runs', workspaceId, accountCompanyId ?? null]
        : ['client-handover-runs']) as readonly unknown[],
    handoverRunsPrefix: () => ['client-handover-runs'] as const,
    // Keyed by the person leaving, not by the Account: this read answers what
    // one colleague still holds across the whole workspace.
    handoverCoverage: (outgoingUserId: string | null | undefined, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-handover-coverage', workspaceId, outgoingUserId ?? null]
        : ['client-handover-coverage']) as readonly unknown[],
    handoverCoveragePrefix: () => ['client-handover-coverage'] as const,
    serviceCatalogue: (workspaceId?: string | null) =>
      (workspaceId
        ? ['services-catalogue', workspaceId]
        : ['services-catalogue']) as readonly unknown[],
    galaxy: (workspaceId?: string | null) =>
      (workspaceId
        ? ['client-galaxy-deals', workspaceId]
        : ['client-galaxy-deals']) as readonly unknown[],
    overdueCommitments: (dealId: string | null, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-overdue-commitments', workspaceId, dealId]
        : ['client-overdue-commitments']) as readonly unknown[],
    commitments: (dealId: string | null, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-commitments', workspaceId, dealId]
        : ['client-commitments']) as readonly unknown[],
    aiCoach: (dealId: string | null, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-ai-coach', workspaceId, dealId]
        : ['client-ai-coach']) as readonly unknown[],
    engagementTrend: (contactId: string | null, workspaceId?: string | null) =>
      (workspaceId
        ? ['client-engagement-trend', workspaceId, contactId]
        : ['client-engagement-trend']) as readonly unknown[],
  },
  reporting: {
    // The window is part of the identity: the same workspace over two different
    // periods is two different answers, not a stale one.
    movementTotals: (from: string, until: string, workspaceId?: string | null) =>
      (workspaceId
        ? ['commercial-movement-totals', workspaceId, from, until]
        : ['commercial-movement-totals']) as readonly unknown[],
    effectiveAgreements: (asOf: string, workspaceId?: string | null) =>
      (workspaceId
        ? ['commercial-effective-agreements', workspaceId, asOf]
        : ['commercial-effective-agreements']) as readonly unknown[],
    billingCoverage: (asOf: string, workspaceId?: string | null) =>
      (workspaceId
        ? ['commercial-billing-coverage', workspaceId, asOf]
        : ['commercial-billing-coverage']) as readonly unknown[],
    recurringRevenuePrefix: () => ['commercial-movement-totals'] as const,
    accountMovementTotals: (from: string, until: string, workspaceId?: string | null) =>
      (workspaceId
        ? ['commercial-account-movement-totals', workspaceId, from, until]
        : ['commercial-account-movement-totals']) as readonly unknown[],
    cohortRetention: (asOf: string, workspaceId?: string | null) =>
      (workspaceId
        ? ['commercial-cohort-retention', workspaceId, asOf]
        : ['commercial-cohort-retention']) as readonly unknown[],
  },
  activities: {
    contact: (contactId: string) => ['contact-activities', contactId] as const,
    contactPrefix: () => ['contact-activities'] as const,
  },
  dealHealth: {
    map: (methodologyId: string, pipelineId: string, ownerId?: string | null) =>
      ['deal-health-map', methodologyId, pipelineId, ownerId ?? 'self'] as const,
    mapPrefix: () => ['deal-health-map'] as const,
    velocity: (pipelineId: string, ownerId?: string | null) =>
      ['deal-velocity', pipelineId, ownerId ?? 'self'] as const,
    velocityDetail: (dealId: string) => ['deal-velocity', dealId] as const,
    velocityPrefix: () => ['deal-velocity'] as const,
  },
  bulkJobs: {
    all: () => ['bulk-jobs'] as const,
    activeForUser: (userId: string | null | undefined) =>
      ['bulk-jobs', 'active', userId ?? 'anon'] as const,
    detail: (jobId: string) => ['bulk-jobs', 'detail', jobId] as const,
    detailAll: () => ['bulk-jobs', 'detail'] as const,
    items: (jobId: string) => ['bulk-jobs', 'items', jobId] as const,
  },
  superbrain: {
    learning: (userId?: string | null, workspaceId?: string | null) =>
      ['superbrain', 'learning', userId ?? 'anon', workspaceId ?? 'global'] as const,
    valueSnapshot: (userId?: string | null) =>
      ['superbrain', 'value-snapshot', userId ?? 'anon'] as const,
    memories: (userId?: string | null, limit?: number) =>
      ['superbrain', 'memories', userId ?? 'anon', limit ?? 12] as const,
    operatingLoop: (userId?: string | null, workspaceId?: string | null) =>
      ['superbrain', 'operating-loop', userId ?? 'anon', workspaceId ?? 'global'] as const,
    suggestionLifecycle: (userId?: string | null, workspaceId?: string | null) =>
      ['superbrain', 'suggestion-lifecycle', userId ?? 'anon', workspaceId ?? 'global'] as const,
    eventIntelligence: (userId?: string | null, workspaceId?: string | null) =>
      ['superbrain', 'event-intelligence', userId ?? 'anon', workspaceId ?? 'global'] as const,
    workspaceEventIntelligence: (workspaceId?: string | null) =>
      ['superbrain', 'workspace-event-intelligence', workspaceId ?? 'global'] as const,
  },
  accountAgents: {
    all: () => ['account-agents'] as const,
    contactAccount: (contactId?: string | null, workspaceId?: string | null, userId?: string | null) =>
      ['account-agents', 'contact-account', contactId ?? 'none', workspaceId ?? 'global', userId ?? 'anon'] as const,
    assignment: (companyId?: string | null, workspaceId?: string | null, userId?: string | null) =>
      ['account-agents', 'assignment', companyId ?? 'none', workspaceId ?? 'global', userId ?? 'anon'] as const,
    memories: (companyId?: string | null, workspaceId?: string | null, userId?: string | null) =>
      ['account-agents', 'memories', companyId ?? 'none', workspaceId ?? 'global', userId ?? 'anon'] as const,
    contacts: (companyId?: string | null, workspaceId?: string | null) =>
      ['account-agents', 'contacts', companyId ?? 'none', workspaceId ?? 'global'] as const,
    whaleOpportunities: (companyId?: string | null, workspaceId?: string | null) =>
      ['account-agents', 'whale-opportunities', companyId ?? 'none', workspaceId ?? 'global'] as const,
    dashboard: (workspaceId?: string | null, userId?: string | null) =>
      ['account-agents', 'dashboard', workspaceId ?? 'global', userId ?? 'anon'] as const,
  },
  assistWorkpieces: {
    all: () => ['assist-workpieces'] as const,
    byCompany: (companyId?: string | null) =>
      ['assist-workpieces', 'company', companyId ?? 'none'] as const,
    byConversation: (conversationId?: string | null, userId?: string | null) =>
      ['assist-workpieces', 'conversation', conversationId ?? 'none', userId ?? 'anon'] as const,
    deck: (
      userId?: string | null,
      workspaceId?: string | null,
      conversationId?: string | null,
      sourceTurnId?: string | null,
      subject?: string | null,
      requestedFocus?: string | null,
      presentationUseCase?: string | null,
      audience?: string | null,
    ) =>
      [
        'assist-workpieces',
        'deck',
        userId ?? 'anon',
        workspaceId ?? 'global',
        conversationId ?? 'new',
        sourceTurnId ?? 'none',
        subject ?? 'unknown',
        requestedFocus ?? 'open-focus',
        presentationUseCase ?? 'unknown-moment',
        audience ?? 'open-audience',
      ] as const,
  },
  assist: {
    recordSearch: (
      entityType: AssistRecordSearchType,
      searchTerm: string,
      workspaceId?: string | null,
    ) => ['assist', 'recordSearch', entityType, searchTerm, workspaceId ?? 'default'] as const,
  },
  email: {
    history: (contactId: string, limit: number = 10) =>
      ['email', 'history', contactId, limit] as const,
  },
  /**
   * Server-sealed Daily Relationship Queue (Today). Preference saves invalidate
   * the prefix so the next load re-reads the sealed day; the edge function does
   * not regenerate terminal days on preference change.
   */
  dailyRelationshipQueue: {
    all: () => ['daily-relationship-queue'] as const,
    day: (userId?: string | null, timezone?: string | null) =>
      ['daily-relationship-queue', userId ?? 'anon', timezone ?? 'UTC'] as const,
  },
  relationshipStates: {
    all: () => ['relationship-states'] as const,
    byContacts: (contactIds: readonly string[]) =>
      ['relationship-states', ...contactIds] as const,
  },
  dealOutlooks: {
    all: () => ['deal-outlooks'] as const,
    byDeals: (dealIds: readonly string[]) =>
      ['deal-outlooks', ...dealIds] as const,
  },
  workspaceDealClosureStats: {
    all: () => ['workspace-deal-closure-stats'] as const,
    byWorkspace: (workspaceId?: string | null) =>
      ['workspace-deal-closure-stats', workspaceId ?? 'none'] as const,
  },
  dealOutcomeRepairQueue: {
    all: () => ['deal-outcome-repair-queue'] as const,
    byWorkspace: (workspaceId?: string | null, status?: string | null) =>
      ['deal-outcome-repair-queue', workspaceId ?? 'none', status ?? 'pending'] as const,
  },
  clientCloseAudit: {
    all: () => ['client-close-audit'] as const,
    coverage: (workspaceId?: string | null) =>
      ['client-close-audit', 'coverage', workspaceId ?? 'none'] as const,
    history: (workspaceId?: string | null) =>
      ['client-close-audit', 'history', workspaceId ?? 'none'] as const,
  },
} as const;
